export type FoldPosition = 'Above' | 'Below' | 'Unknown';

/**
 * Tells whether an element was inside the first screenful at page load
 * ("above the fold") or whether the user had to scroll to reach it.
 *
 * The element's position is resolved against the document, not the current
 * viewport, so the answer does not change as the user scrolls. Elements
 * inside a scrollable sub-container are measured against the document fold
 * too, which is the closest meaningful approximation for that layout.
 *
 * @returns 'Unknown' when the element has no measurable layout
 */
export function getFoldPosition(
    element: Element | null | undefined,
): FoldPosition {
    if (!element || !element.isConnected) {
        return 'Unknown';
    }

    // `innerHeight` is the visible height; `clientHeight` (layout viewport)
    // is only a fallback, as it ignores mobile browser chrome and reads larger.
    const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;
    const rect = element.getBoundingClientRect();

    if (!viewportHeight || (!rect.width && !rect.height)) {
        return 'Unknown';
    }

    const documentTop = rect.top + window.scrollY;

    return documentTop < viewportHeight ? 'Above' : 'Below';
}
